import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Form, Table, Modal, Badge, Alert, Tab, Tabs, TextArea, Spinner } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import facilitatorService from '../services/facilitatorService';
import programsService from '../services/programsService';
import { db } from '../firebase';
import { collection, query, where, getDocs, addDoc, serverTimestamp, orderBy, limit } from 'firebase/firestore';

const SessionLogger = () => {
  const { currentUser } = useAuth();
  const [showLogModal, setShowLogModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionLog, setSessionLog] = useState({
    sessionId: '',
    moduleId: '',
    lessonId: '',
    sessionDate: '',
    overallSessionNotes: '',
    sessionRating: 5,
    recommendedImprovements: [''],
    learnerLogs: {}
  });

  const [showNonResponsiveModal, setShowNonResponsiveModal] = useState(false);
  const [nonResponsiveEntry, setNonResponsiveEntry] = useState({
    learner: '',
    sessionId: '',
    status: 'no_response',
    attempts: [],
    facilitatorActions: [],
    followUpNotes: ''
  });

  // Real data state
  const [learners, setLearners] = useState([]);
  const [sessionLogs, setSessionLogs] = useState([]);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [nonResponsiveLogs, setNonResponsiveLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load data on component mount
  useEffect(() => {
    if (currentUser) {
      loadData();
    }
  }, [currentUser]);

  // Load all required data
  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadLearners(),
        loadPrograms(),
        loadSessionLogs(),
        loadUpcomingSessions(),
        loadNonResponsiveLogs()
      ]);
    } catch (error) {
      devError('Error loading session data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load learners from facilitator's assigned programs
  const loadLearners = async () => {
    try {
      const learnersData = await facilitatorService.getLearnersByAssignedPrograms(currentUser.uid);
      setLearners(learnersData);
    } catch (error) {
      devError('Error loading learners:', error);
    }
  };

  // Load programs
  const loadPrograms = async () => {
    try {
      const programsData = await programsService.getPrograms(true);
      setPrograms(programsData);
    } catch (error) {
      devError('Error loading programs:', error);
    }
  };

  // Load session logs from database
  const loadSessionLogs = async () => {
    try {
      const q = query(
        collection(db, 'sessionLogs'),
        where('facilitatorId', '==', currentUser.uid),
        orderBy('sessionDate', 'desc'),
        limit(50)
      );

      const querySnapshot = await getDocs(q);
      const logs = [];

      querySnapshot.forEach((doc) => {
        logs.push({
          id: doc.id,
          ...doc.data(),
          sessionDate: doc.data().sessionDate?.toDate()?.toISOString() || null
        });
      });

      setSessionLogs(logs);
    } catch (error) {
      devError('Error loading session logs:', error);
    }
  };

  // Load upcoming sessions from database
  const loadUpcomingSessions = async () => {
    try {
      const q = query(
        collection(db, 'sessions'),
        where('facilitatorId', '==', currentUser.uid),
        orderBy('scheduledDate', 'asc')
      );

      const querySnapshot = await getDocs(q);
      const sessions = [];

      querySnapshot.forEach((doc) => {
        const sessionData = doc.data();
        const sessionDate = sessionData.scheduledDate?.toDate();

        // Only include sessions that are today or in the past (ready for logging)
        if (sessionDate && sessionDate <= new Date()) {
          sessions.push({
            id: doc.id,
            ...sessionData,
            scheduledDate: sessionDate.toISOString()
          });
        }
      });

      setUpcomingSessions(sessions);
    } catch (error) {
      devError('Error loading upcoming sessions:', error);
      // Fallback: create mock session for demo purposes
      setUpcomingSessions([
        {
          id: 'demo_session_1',
          moduleId: 'demo_module',
          lessonId: 'demo_lesson',
          scheduledDate: new Date().toISOString(),
          title: 'Demo Session - Digital Literacy Basics'
        }
      ]);
    }
  };

  // Load non-responsive logs from database
  const loadNonResponsiveLogs = async () => {
    try {
      const q = query(
        collection(db, 'nonResponsiveLogs'),
        where('facilitatorId', '==', currentUser.uid),
        orderBy('loggedAt', 'desc'),
        limit(20)
      );

      const querySnapshot = await getDocs(q);
      const logs = [];

      querySnapshot.forEach((doc) => {
        logs.push({
          id: doc.id,
          ...doc.data(),
          loggedAt: doc.data().loggedAt?.toDate()?.toISOString() || null
        });
      });

      setNonResponsiveLogs(logs);
    } catch (error) {
      devError('Error loading non-responsive logs:', error);
    }
  };

  const initializeLearnerLogs = (sessionId) => {
    const logs = {};
    learners.forEach(learner => {
      const learnerId = learner.id || learner.uid;
      logs[learnerId] = {
        attendance: 'present',
        participation: 'medium',
        successes: [''],
        challenges: [''],
        notes: '',
        recommendedActions: ['']
      };
    });
    return logs;
  };

  const handleStartSessionLog = (session) => {
    setSelectedSession(session);
    setSessionLog({
      sessionId: session.id,
      moduleId: session.moduleId,
      lessonId: session.lessonId,
      sessionDate: session.scheduledDate,
      overallSessionNotes: '',
      sessionRating: 5,
      recommendedImprovements: [''],
      learnerLogs: initializeLearnerLogs(session.id)
    });
    setShowLogModal(true);
  };

  const updateLearnerLog = (learnerId, field, value, index = null) => {
    setSessionLog(prev => ({
      ...prev,
      learnerLogs: {
        ...prev.learnerLogs,
        [learnerId]: {
          ...prev.learnerLogs[learnerId],
          [field]: index !== null ? 
            prev.learnerLogs[learnerId][field].map((item, i) => i === index ? value : item) :
            value
        }
      }
    }));
  };

  const addArrayItem = (learnerId, field) => {
    setSessionLog(prev => ({
      ...prev,
      learnerLogs: {
        ...prev.learnerLogs,
        [learnerId]: {
          ...prev.learnerLogs[learnerId],
          [field]: [...prev.learnerLogs[learnerId][field], '']
        }
      }
    }));
  };

  const removeArrayItem = (learnerId, field, index) => {
    setSessionLog(prev => ({
      ...prev,
      learnerLogs: {
        ...prev.learnerLogs,
        [learnerId]: {
          ...prev.learnerLogs[learnerId],
          [field]: prev.learnerLogs[learnerId][field].filter((_, i) => i !== index)
        }
      }
    }));
  };

  const handleSaveSessionLog = async () => {
    try {
      // Save session log to Firebase
      const logDoc = {
        ...sessionLog,
        facilitatorId: currentUser.uid,
        facilitatorName: currentUser.displayName,
        sessionDate: new Date(sessionLog.sessionDate),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'sessionLogs'), logDoc);
      devLog('Session log saved successfully:', docRef.id);

      // Refresh session logs
      await loadSessionLogs();

      setShowLogModal(false);
      setSelectedSession(null);
    } catch (error) {
      devError('Error saving session log:', error);
      alert('Failed to save session log. Please try again.');
    }
  };

  const handleLogNonResponsive = async () => {
    try {
      // Save non-responsive log to Firebase
      const nonResponsiveDoc = {
        ...nonResponsiveEntry,
        facilitatorId: currentUser.uid,
        facilitatorName: currentUser.displayName,
        loggedAt: serverTimestamp(),
        createdAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'nonResponsiveLogs'), nonResponsiveDoc);
      devLog('Non-responsive log saved successfully:', docRef.id);

      setShowNonResponsiveModal(false);
      setNonResponsiveEntry({
        learner: '',
        sessionId: '',
        status: 'no_response',
        attempts: [],
        facilitatorActions: [],
        followUpNotes: ''
      });
    } catch (error) {
      devError('Error saving non-responsive log:', error);
      alert('Failed to save non-responsive log. Please try again.');
    }
  };

  const getParticipationColor = (level) => {
    switch (level) {
      case 'high': return 'success';
      case 'medium': return 'warning';
      case 'low': return 'danger';
      default: return 'secondary';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return 'success';
      case 'absent': return 'danger';
      case 'late': return 'warning';
      case 'no_response': return 'danger';
      case 'technical_issues': return 'info';
      default: return 'secondary';
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="session-logger">
      <Tabs defaultActiveKey="current-sessions" id="session-logger-tabs" className="mb-4">
        <Tab eventKey="current-sessions" title="Current Sessions">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Recent/Current Sessions</h5>
              <Button 
                variant="primary" 
                onClick={() => setShowNonResponsiveModal(true)}
              >
                Log Non-Responsive Learner
              </Button>
            </Card.Header>
            <Card.Body>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Session</th>
                    <th>Date/Time</th>
                    <th>Facilitator</th>
                    <th>Module</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {upcomingSessions.map(session => {
                    const module = programs.find(p => p.modules && p.modules.includes(session.moduleId));
                    const hasLog = sessionLogs.find(log => log.sessionId === session.id);
                    return (
                      <tr key={session.id}>
                        <td>
                          <strong>{session.title}</strong>
                          <br />
                          <small className="text-muted">Session ID: {session.id}</small>
                        </td>
                        <td>
                          {new Date(session.scheduledDate).toLocaleDateString()}
                          <br />
                          <small className="text-muted">
                            {new Date(session.scheduledDate).toLocaleTimeString()}
                          </small>
                        </td>
                        <td>{session.facilitator}</td>
                        <td>
                          <Badge bg="info">{module?.moduleName}</Badge>
                        </td>
                        <td>
                          {hasLog ? (
                            <Badge bg="success">Logged</Badge>
                          ) : (
                            <Badge bg="warning">Pending</Badge>
                          )}
                        </td>
                        <td>
                          <div className="d-flex flex-column gap-1">
                            <Button 
                              variant={hasLog ? "outline-primary" : "primary"}
                              size="sm"
                              onClick={() => handleStartSessionLog(session)}
                            >
                              {hasLog ? 'View/Edit Log' : 'Create Log'}
                            </Button>
                            <Button variant="outline-secondary" size="sm">
                              View Attendance
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="session-history" title="Session History">
          <Card>
            <Card.Header>
              <h5 className="mb-0">Session Logs History</h5>
            </Card.Header>
            <Card.Body>
              {sessionLogs.map(log => (
                <Card key={log.id} className="mb-3">
                  <Card.Header>
                    <Row className="align-items-center">
                      <Col>
                        <h6 className="mb-0">Session {log.sessionId}</h6>
                        <small className="text-muted">
                          {new Date(log.sessionDate).toLocaleDateString()} - {log.facilitator}
                        </small>
                      </Col>
                      <Col xs="auto">
                        <Badge bg="warning">Rating: {log.sessionRating}/5</Badge>
                      </Col>
                    </Row>
                  </Card.Header>
                  <Card.Body>
                    <p><strong>Overall Notes:</strong> {log.overallSessionNotes}</p>
                    
                    <h6>Learner Performance:</h6>
                    <Table size="sm" className="mb-3">
                      <thead>
                        <tr>
                          <th>Learner</th>
                          <th>Attendance</th>
                          <th>Participation</th>
                          <th>Successes</th>
                          <th>Challenges</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(log.learnerLogs).map(([learnerId, learnerLog]) => {
                          const learner = learners.find(l => (l.id || l.uid) === learnerId);
                          return (
                            <tr key={learnerId}>
                              <td>{learner?.displayName}</td>
                              <td>
                                <Badge bg={getStatusColor(learnerLog.attendance)}>
                                  {learnerLog.attendance}
                                </Badge>
                              </td>
                              <td>
                                <Badge bg={getParticipationColor(learnerLog.participation)}>
                                  {learnerLog.participation}
                                </Badge>
                              </td>
                              <td>
                                <ul className="mb-0 small">
                                  {learnerLog.successes.map((success, idx) => (
                                    <li key={idx}>{success}</li>
                                  ))}
                                </ul>
                              </td>
                              <td>
                                <ul className="mb-0 small">
                                  {learnerLog.challenges.map((challenge, idx) => (
                                    <li key={idx}>{challenge}</li>
                                  ))}
                                </ul>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>

                    <h6>Recommended Improvements:</h6>
                    <ul>
                      {log.recommendedImprovements.map((improvement, idx) => (
                        <li key={idx}>{improvement}</li>
                      ))}
                    </ul>
                  </Card.Body>
                </Card>
              ))}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="non-responsive" title="Non-Responsive Learners">
          <Card>
            <Card.Header>
              <h5 className="mb-0">Non-Responsive Learner Log</h5>
            </Card.Header>
            <Card.Body>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Learner</th>
                    <th>Session</th>
                    <th>Expected Date</th>
                    <th>Status</th>
                    <th>Attempts Made</th>
                    <th>Follow-up Required</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {nonResponsiveLogs.map(entry => {
                    const learner = learners.find(l => (l.id || l.uid) === entry.learner);
                    return (
                      <tr key={entry.id}>
                        <td>{learner?.displayName || 'Unknown'}</td>
                        <td>{entry.sessionId}</td>
                        <td>{new Date(entry.expectedDate).toLocaleDateString()}</td>
                        <td>
                          <Badge bg={getStatusColor(entry.status)}>
                            {entry.status.replace('_', ' ')}
                          </Badge>
                        </td>
                        <td>{entry.attempts.length} attempts</td>
                        <td>
                          {entry.followUpRequired ? (
                            <Badge bg="warning">Yes</Badge>
                          ) : (
                            <Badge bg="success">No</Badge>
                          )}
                        </td>
                        <td>
                          <Button variant="outline-primary" size="sm">
                            View Details
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Session Log Modal */}
      <Modal show={showLogModal} onHide={() => setShowLogModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Session Log: {selectedSession?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {selectedSession && (
            <div>
              <Row className="mb-4">
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Overall Session Notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={sessionLog.overallSessionNotes}
                      onChange={(e) => setSessionLog(prev => ({...prev, overallSessionNotes: e.target.value}))}
                      placeholder="Enter overall session observations..."
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Session Rating (1-5)</Form.Label>
                    <Form.Select
                      value={sessionLog.sessionRating}
                      onChange={(e) => setSessionLog(prev => ({...prev, sessionRating: parseInt(e.target.value)}))}
                    >
                      <option value={1}>1 - Poor</option>
                      <option value={2}>2 - Below Average</option>
                      <option value={3}>3 - Average</option>
                      <option value={4}>4 - Good</option>
                      <option value={5}>5 - Excellent</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <h6>Individual Learner Logs:</h6>
              {learners.map(learner => (
                <Card key={learner.uid} className="mb-3">
                  <Card.Header>
                    <h6 className="mb-0">{learner.displayName}</h6>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>Attendance</Form.Label>
                          <Form.Select
                            value={sessionLog.learnerLogs[learner.uid]?.attendance || 'present'}
                            onChange={(e) => updateLearnerLog(learner.uid, 'attendance', e.target.value)}
                          >
                            <option value="present">Present</option>
                            <option value="absent">Absent</option>
                            <option value="late">Late</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>Participation</Form.Label>
                          <Form.Select
                            value={sessionLog.learnerLogs[learner.uid]?.participation || 'medium'}
                            onChange={(e) => updateLearnerLog(learner.uid, 'participation', e.target.value)}
                          >
                            <option value="high">High</option>
                            <option value="medium">Medium</option>
                            <option value="low">Low</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Notes</Form.Label>
                          <Form.Control
                            type="text"
                            value={sessionLog.learnerLogs[learner.uid]?.notes || ''}
                            onChange={(e) => updateLearnerLog(learner.uid, 'notes', e.target.value)}
                            placeholder="General observations about this learner..."
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Label>Successes</Form.Label>
                        {(sessionLog.learnerLogs[learner.uid]?.successes || ['']).map((success, idx) => (
                          <div key={idx} className="d-flex mb-2">
                            <Form.Control
                              type="text"
                              value={success}
                              onChange={(e) => updateLearnerLog(learner.uid, 'successes', e.target.value, idx)}
                              placeholder="What went well..."
                            />
                            <Button 
                              variant="outline-danger" 
                              size="sm" 
                              className="ms-2"
                              onClick={() => removeArrayItem(learner.uid, 'successes', idx)}
                            >
                              ×
                            </Button>
                          </div>
                        ))}
                        <Button 
                          variant="outline-success" 
                          size="sm"
                          onClick={() => addArrayItem(learner.uid, 'successes')}
                        >
                          + Add Success
                        </Button>
                      </Col>
                      <Col md={6}>
                        <Form.Label>Challenges</Form.Label>
                        {(sessionLog.learnerLogs[learner.uid]?.challenges || ['']).map((challenge, idx) => (
                          <div key={idx} className="d-flex mb-2">
                            <Form.Control
                              type="text"
                              value={challenge}
                              onChange={(e) => updateLearnerLog(learner.uid, 'challenges', e.target.value, idx)}
                              placeholder="Areas for improvement..."
                            />
                            <Button 
                              variant="outline-danger" 
                              size="sm" 
                              className="ms-2"
                              onClick={() => removeArrayItem(learner.uid, 'challenges', idx)}
                            >
                              ×
                            </Button>
                          </div>
                        ))}
                        <Button 
                          variant="outline-warning" 
                          size="sm"
                          onClick={() => addArrayItem(learner.uid, 'challenges')}
                        >
                          + Add Challenge
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLogModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveSessionLog}>
            Save Session Log
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Non-Responsive Learner Modal */}
      <Modal show={showNonResponsiveModal} onHide={() => setShowNonResponsiveModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Log Non-Responsive Learner</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Learner</Form.Label>
                  <Form.Select
                    value={nonResponsiveEntry.learner}
                    onChange={(e) => setNonResponsiveEntry(prev => ({...prev, learner: e.target.value}))}
                  >
                    <option value="">Select learner...</option>
                    {learners.map(learner => (
                      <option key={learner.uid} value={learner.uid}>
                        {learner.displayName}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Session ID</Form.Label>
                  <Form.Select
                    value={nonResponsiveEntry.sessionId}
                    onChange={(e) => setNonResponsiveEntry(prev => ({...prev, sessionId: e.target.value}))}
                  >
                    <option value="">Select session...</option>
                    {upcomingSessions.map(session => (
                      <option key={session.id} value={session.id}>
                        {session.title} - {new Date(session.scheduledDate).toLocaleDateString()}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={nonResponsiveEntry.status}
                    onChange={(e) => setNonResponsiveEntry(prev => ({...prev, status: e.target.value}))}
                  >
                    <option value="no_response">No Response</option>
                    <option value="late_response">Late Response</option>
                    <option value="absent">Confirmed Absent</option>
                    <option value="technical_issues">Technical Issues</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Follow-up Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={nonResponsiveEntry.followUpNotes}
                onChange={(e) => setNonResponsiveEntry(prev => ({...prev, followUpNotes: e.target.value}))}
                placeholder="What follow-up actions are needed?"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNonResponsiveModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleLogNonResponsive}>
            Log Entry
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SessionLogger;